import MiniSearch from 'minisearch'
import { tokenize } from './textNormalize'

// Build memoised MiniSearch indices from Redux-derived collections.
//
// MiniSearch is a small in-memory full-text engine that uses BM25 scoring
// over a configurable per-token analyser. It is intentionally a closer
// behavioural match to Algolia than `buildFuseIndices.js` (which uses
// Fuse's character-level Bitap algorithm), and is kept as a separate
// secondary local provider so the two can be evaluated side-by-side
// against Algolia at runtime through `searchReporter`.
//
// Each MiniSearch instance is shaped to mirror the corresponding Algolia
// index — `repos`, `folders`, `sets_questions` — so the same UI primitives
// (`IndexHitList` / `SectionHeader` / per-index totals) can render results
// from any of the three engines without branching.
//
// Hit shape is the original Redux record spread + the standard
// `__localOrigin: true` + `objectID` markers used by all local providers,
// plus a `__localEngine: 'minisearch'` discriminator that the UI reads to
// render a distinct badge variant.

// ─── Tokenization & analyser ───────────────────────────────────────────────

// `tokenize` is the shared Unicode-aware tokenizer (diacritic folding + CJK /
// no-space-script segmentation). MiniSearch applies it to BOTH documents and
// queries, so both sides stay symmetric. Because folding already happens
// inside `tokenize`, `processTerm` only needs to drop empty tokens.
function processTerm(term) {
  if (typeof term !== 'string') return false
  return term.length > 0 ? term : false
}

// ─── Search options ────────────────────────────────────────────────────────
//
// `prefix: true`  — Algolia `prefixLast` equivalent. A query token "lib"
//                   matches "library", "liberation", etc.
// `fuzzy: 0.2`    — Levenshtein tolerance ~20% of token length. A 4-char
//                   query allows ~1 typo, an 8-char query allows ~2 typos —
//                   close to Algolia's `minWordSizefor1Typo: 4` /
//                   `minWordSizefor2Typos: 8` configuration.
// `combineWith`   — 'AND' so multi-token queries require every token to
//                   match somewhere in the document (Algolia default).
const DEFAULT_SEARCH_OPTIONS = {
  prefix: true,
  fuzzy: 0.2,
  combineWith: 'AND',
}

// ─── Per-domain index builders ─────────────────────────────────────────────
//
// Each builder produces an independent MiniSearch instance with field
// boosts that mirror `TEMP_LOCAL_SEARCH_INDEXES.md`. The `_kind` field
// labels every document so the unified `sets_questions` index can keep
// the set/question discriminator implicit (matches Algolia's behaviour).
// `_original` carries the source Redux record back into the hit; this
// trades memory for code simplicity and avoids a parallel id→record map.

function notArchived(item) {
  return item && !item.archived && !item.parentArchived
}

function joinNonEmpty(arrayOfStrings) {
  if (!Array.isArray(arrayOfStrings)) return ''
  return arrayOfStrings.filter((s) => typeof s === 'string' && s.length > 0).join(' ')
}

function buildRepoDocs(repos) {
  return repos.filter(notArchived).map((repo) => {
    const author = repo.author || {}
    return {
      msId: `repo-${repo.id}`,
      name: repo.name,
      description: repo.description,
      authorFirstName: author.firstName,
      authorLastName: author.lastName,
      _kind: 'repo',
      _original: repo,
    }
  })
}

function buildFolderDocs(folders) {
  return folders.filter(notArchived).map((folder) => ({
    msId: `folder-${folder.id}`,
    name: folder.name,
    _kind: 'folder',
    _original: folder,
  }))
}

function buildSetDocs(sets) {
  return sets.filter(notArchived).map((set) => {
    const questions = Array.isArray(set.questions) ? set.questions : []
    const bodies = questions.map((q) => (q ? q.body : ''))
    const choices = []
    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i]
      if (q && Array.isArray(q.choices)) {
        for (let j = 0; j < q.choices.length; j += 1) {
          const c = q.choices[j]
          if (c && typeof c.body === 'string') choices.push(c.body)
        }
      }
    }
    return {
      msId: `set-${set.id}`,
      name: set.name,
      questionBodies: joinNonEmpty(bodies),
      questionChoices: joinNonEmpty(choices),
      _kind: 'set',
      _original: set,
    }
  })
}

function buildQuestionDocs(questions) {
  return questions.filter(notArchived).map((question) => {
    const choices = Array.isArray(question.choices) ? question.choices : []
    const choiceBodies = choices.map((c) => (c ? c.body : ''))
    return {
      msId: `question-${question.id}`,
      body: question.body,
      choices: joinNonEmpty(choiceBodies),
      _kind: 'question',
      _original: question,
    }
  })
}

const REPOS_OPTIONS = {
  idField: 'msId',
  fields: ['name', 'description', 'authorFirstName', 'authorLastName'],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: {
    ...DEFAULT_SEARCH_OPTIONS,
    boost: {
      name: 3, description: 1, authorFirstName: 1, authorLastName: 1,
    },
  },
}

const FOLDERS_OPTIONS = {
  idField: 'msId',
  fields: ['name'],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...DEFAULT_SEARCH_OPTIONS, boost: { name: 1 } },
}

const SETS_QUESTIONS_OPTIONS = {
  idField: 'msId',
  fields: [
    'name', 'questionBodies', 'questionChoices',
    'body', 'choices',
  ],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: {
    ...DEFAULT_SEARCH_OPTIONS,
    boost: {
      name: 3,
      questionBodies: 2,
      questionChoices: 1,
      body: 3,
      choices: 1,
    },
  },
}

// ─── Memoisation cache ─────────────────────────────────────────────────────
//
// Same WeakMap-by-input-list-reference strategy as the Fuse-side builder.
// Redux replaces the array reference whenever any item changes; the cache
// returns the same MiniSearch instance until that happens.

const reposCache = new WeakMap()
const foldersCache = new WeakMap()
const setsQuestionsCache = new WeakMap()

function getRepoIndex(repos) {
  const list = Array.isArray(repos) ? repos : []
  const cached = reposCache.get(list)
  if (cached) return cached
  const ms = new MiniSearch(REPOS_OPTIONS)
  ms.addAll(buildRepoDocs(list))
  reposCache.set(list, ms)
  return ms
}

function getFolderIndex(folders) {
  const list = Array.isArray(folders) ? folders : []
  const cached = foldersCache.get(list)
  if (cached) return cached
  const ms = new MiniSearch(FOLDERS_OPTIONS)
  ms.addAll(buildFolderDocs(list))
  foldersCache.set(list, ms)
  return ms
}

function getSetsQuestionsIndex(sets, questions) {
  const setsList = Array.isArray(sets) ? sets : []
  const questionsList = Array.isArray(questions) ? questions : []
  const cached = setsQuestionsCache.get(setsList)
  if (cached && cached.questions === questionsList) return cached.ms
  const ms = new MiniSearch(SETS_QUESTIONS_OPTIONS)
  ms.addAll([...buildSetDocs(setsList), ...buildQuestionDocs(questionsList)])
  setsQuestionsCache.set(setsList, { questions: questionsList, ms })
  return ms
}

/**
 * Build per-domain MiniSearch indices from Redux collections.
 *
 * @returns {{ repos: MiniSearch, folders: MiniSearch, setsQuestions: MiniSearch }}
 */
export function buildMiniSearchIndices({
  sets, questions, folders, repos,
}) {
  return {
    repos: getRepoIndex(repos),
    folders: getFolderIndex(folders),
    setsQuestions: getSetsQuestionsIndex(sets, questions),
  }
}

// ─── Hit projection ────────────────────────────────────────────────────────

function toHit(result) {
  const original = result._original || {}
  return {
    ...original,
    objectID: original.id,
    __localOrigin: true,
    __localEngine: 'minisearch',
    __minisearchOrigin: true,
    __minisearchScore: result.score,
    // MiniSearch returns `terms: ['math']` and `match: { math: ['name'] }`.
    // Both are kept on the hit so the searchReporter can render diagnostic
    // tables comparable to Fuse's `__fuseMatches`.
    __minisearchTerms: result.terms,
    __minisearchMatch: result.match,
  }
}

const NOOP = () => {}

function emptyIndexResult() {
  return {
    hits: [], hitCount: 0, hasMore: false, loadMore: NOOP,
  }
}

function runIndex(ms, query, limitPerIndex) {
  if (!query || !ms) return emptyIndexResult()
  const all = ms.search(query)
  const hitCount = all.length
  const slice = (typeof limitPerIndex === 'number' && limitPerIndex > 0) ?
    all.slice(0, limitPerIndex) :
    all
  return {
    hits: slice.map(toHit),
    hitCount,
    hasMore: false,
    loadMore: NOOP,
  }
}

/**
 * Run a query against every MiniSearch index and return per-index results
 * in the same shape as `AlgoliaSearchProvider`'s `indexResults`.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @param {string} query
 * @param {number} [limitPerIndex=10]
 */
export function searchPerMiniSearchIndex(indices, query, limitPerIndex = 10) {
  return {
    repos: runIndex(indices.repos, query, limitPerIndex),
    folders: runIndex(indices.folders, query, limitPerIndex),
    // eslint-disable-next-line camelcase
    sets_questions: runIndex(indices.setsQuestions, query, limitPerIndex),
  }
}
